import AbstractListResource from './abstract/abstract-list-resource';
import {ActionType, PluginType, Resource, ServerStatus as ServerMetrics, Uid} from 'sdk/src/defines';
import PluginInstance from './items/plugin-instance';
import {TFunction} from 'react-i18next';
import {DropDownItem} from '../utility/types';

export type ServerStatusType = ServerMetrics & {
    id: Uid,
    title?: string,
    serverType?: string,
    pluginName?: string,
    host?: string,
    checkedAt?: string,
};

export type CoderChannelTelemetry = {
    name: string,
    state?: number | string,
    process?: string,
    profile?: string,
    middleware_profile_name?: string,
    timeout?: number | string,
};

export type CoderTimelineItem = {
    kind: string,
    code?: string,
    severity: number | string,
    source?: string,
    begin_us: number | string,
    end_us: number | string,
    occurrences?: number | string,
    message?: string,
};

export type CoderTimeline = {
    begin_us: number | string,
    end_us: number | string,
    coverage_kind?: string,
    coverage?: {begin_us: number | string, end_us: number | string}[],
    items?: CoderTimelineItem[],
    summary?: {warnings?: number | string, errors?: number | string, missing?: number | string},
};

export type ServerTelemetry = {
    kind: 'coder' | 'media-server',
    available: boolean,
    reason?: 'not_configured' | 'unauthorized' | 'unreachable',
    fetchedAt?: string,
    status?: ServerMetrics & {channels?: any},
    channels?: CoderChannelTelemetry[],
    selectedChannel?: string,
    timeline?: CoderTimeline,
};

class PluginInstances extends AbstractListResource<PluginInstance> {
    public resourceType: Resource = Resource.PLUGIN_INSTANCES;

    public resourceItemClass: typeof PluginInstance = PluginInstance;

    public allowedActions: ActionType[] = [
        ActionType.LIST,
        ActionType.UPDATE,
        ActionType.CREATE,
        ActionType.DELETE,
    ];

    public forDropDown(t: TFunction, empty?: string, type?: PluginType): DropDownItem[] {
        if (!this.isInitialized()) {
            return [];
        }

        const result: DropDownItem[] = [];

        if (empty) {
            result.push({
                label: empty,
                value: null,
            });
        }

        this.getItems().forEach((item: PluginInstance) => {
            if (!type || item.getPluginType() === type) {
                result.push({
                    label: item.title,
                    value: item.id,
                });
            }
        });

        return result;
    }

    public getPluginInstancesByType(type: PluginType): PluginInstance[] {
        return this.getItems()
            .filter((pluginInstance: PluginInstance) => !type || type === pluginInstance.getPluginType());
    }

    public getServerStatuses(): Promise<ServerStatusType[]> {
        return this.api().get(`/${this.resourceType.toLocaleLowerCase()}/servers/statuses`);
    }

    public getServerTelemetry(id: Uid, params: {channel?: string, fromUs?: number, toUs?: number} = {}): Promise<ServerTelemetry> {
        return this.api().get(`/${this.resourceType.toLocaleLowerCase()}/servers/${id}/telemetry`, params);
    }
}

export {PluginInstances, PluginInstance};

export default new PluginInstances();
